import { useState, useEffect } from "react";
import { Loader2, Sparkles, Search, PenTool, ImageIcon, Target, CheckCircle2 } from "lucide-react";
import { DD } from "./constants";

const LOADING_STEPS = [
  { label: "Analyzing your keywords & GeoGrid data...", icon: <Search className="w-4 h-4" />, duration: 3000 },
  { label: "Researching competitors & weak areas...", icon: <Target className="w-4 h-4" />, duration: 4000 },
  { label: "Generating 20 keyword-optimized posts...", icon: <PenTool className="w-4 h-4" />, duration: 8000 },
  { label: "Scheduling offers, events & updates...", icon: <Sparkles className="w-4 h-4" />, duration: 3000 },
  { label: "Preparing image generation queue...", icon: <ImageIcon className="w-4 h-4" />, duration: 2000 },
];

export function GenerateModal({
  isGenerating,
  onClose,
}: {
  isGenerating: boolean;
  onClose: () => void;
}) {
  const [loadingStep, setLoadingStep] = useState(0);

  useEffect(() => {
    if (!isGenerating) return;
    setLoadingStep(0);
    const timers: ReturnType<typeof setTimeout>[] = [];
    let cumulative = 0;
    LOADING_STEPS.forEach((step, i) => {
      if (i > 0) {
        cumulative += LOADING_STEPS[i - 1].duration;
        timers.push(setTimeout(() => setLoadingStep(i), cumulative));
      }
    });
    return () => timers.forEach(clearTimeout);
  }, [isGenerating]);

  if (!isGenerating) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="generate-modal-overlay">
      <div
        className="w-[95vw] max-w-[480px] rounded-2xl overflow-hidden"
        style={{ background: DD.bg, border: `1px solid ${DD.border}` }}
        onClick={e => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-2 text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl flex items-center justify-center" style={{ background: DD.accentBg, border: `1px solid ${DD.accentBorder}` }}>
            <Sparkles className="w-7 h-7 animate-pulse" style={{ color: DD.accent }} />
          </div>
          <h3 className="text-lg font-bold text-white" data-testid="text-generate-title">Generating Your Posts</h3>
          <p className="text-xs text-white/40 mt-1">AI is building your content calendar using your keywords, GeoGrid data, and business profile</p>
        </div>

        <div className="p-6 space-y-3">
          {LOADING_STEPS.map((step, i) => (
            <div
              key={i}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-500 ${
                i <= loadingStep ? "opacity-100 translate-x-0" : "opacity-30 translate-x-4"
              }`}
              style={{
                background: i === loadingStep ? DD.accentBg : DD.surface,
                border: `1px solid ${i === loadingStep ? DD.accentBorder : DD.border}`,
              }}
              data-testid={`loading-step-${i}`}
            >
              <span style={{ color: i <= loadingStep ? DD.accent : DD.textTertiary }}>{step.icon}</span>
              <span className="text-sm font-medium flex-1" style={{ color: i <= loadingStep ? DD.text : DD.textTertiary }}>
                {step.label}
              </span>
              {i < loadingStep && <CheckCircle2 className="w-4 h-4 text-green-400" />}
              {i === loadingStep && <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: DD.accent }} />}
            </div>
          ))}
        </div>

        <div className="px-6 pb-5 pt-1 text-center">
          <p className="text-[11px] text-white/30">Posts will appear in Drafts & Review for your approval</p>
        </div>
      </div>
    </div>
  );
}
